import { useEffect, useState } from "react";
import axios from "axios";
import { Briefcase, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline block rendered inside CRMStatusBanner showing the up-to-2 most recent
 * Sellsy opportunities attached to the matched contact. Each row has the opp
 * name (linked to Sellsy), and a select to change the pipeline step.
 *
 * Props:
 *   contactSellsyId: string  // numeric Sellsy contact id
 *   companySellsyId: string? // optional company id (passed through to API for hinting)
 */
const ContactOpportunitiesBlock = ({ contactSellsyId, companySellsyId }) => {
  const [loading, setLoading] = useState(true);
  const [opps, setOpps] = useState([]);
  const [savingByOppId, setSavingByOppId] = useState({});

  useEffect(() => {
    let cancelled = false;
    if (!contactSellsyId) return;
    setLoading(true);
    const params = companySellsyId ? `?company_id=${encodeURIComponent(companySellsyId)}` : "";
    axios
      .get(`${API}/contacts/${contactSellsyId}/recent-opportunities${params}`)
      .then((r) => {
        if (!cancelled) setOpps(r.data?.opportunities || []);
      })
      .catch(() => {
        if (!cancelled) setOpps([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contactSellsyId, companySellsyId]);

  const handleStepChange = async (oppId, newStepId, oppName) => {
    setSavingByOppId((s) => ({ ...s, [oppId]: true }));
    // Optimistic UI: update local state immediately
    setOpps((prev) => prev.map((o) => (o.sellsy_id === oppId ? { ...o, current_step_id: newStepId, current_step_name: o.available_steps.find((s) => s.id === newStepId)?.name || o.current_step_name } : o)));
    try {
      await axios.put(`${API}/opportunities/${oppId}/step`, { step_id: newStepId });
      toast.success(`Étape mise à jour pour "${oppName}"`);
    } catch (e) {
      toast.error("Echec mise à jour de l'étape Sellsy");
      // Rollback by re-fetching
      try {
        const params = companySellsyId ? `?company_id=${encodeURIComponent(companySellsyId)}` : "";
        const r = await axios.get(`${API}/contacts/${contactSellsyId}/recent-opportunities${params}`);
        setOpps(r.data?.opportunities || []);
      } catch (_) { /* ignore */ }
    } finally {
      setSavingByOppId((s) => ({ ...s, [oppId]: false }));
    }
  };

  if (!contactSellsyId) return null;

  if (loading) {
    return (
      <div className="text-xs text-slate-500 flex items-center gap-1.5 px-1" data-testid="contact-opps-loading">
        <Loader2 className="w-3 h-3 animate-spin" />
        Chargement des opportunités...
      </div>
    );
  }

  if (opps.length === 0) {
    return (
      <div
        className="text-xs text-slate-600 italic bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 flex items-center gap-1.5"
        data-testid="contact-opps-empty"
      >
        <Briefcase className="w-3 h-3 flex-shrink-0" />
        Aucune opportunité rattachée à ce client
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="contact-opps-list">
      <p className="text-[10px] uppercase tracking-wide font-medium text-slate-500 mt-1 flex items-center gap-1">
        <Briefcase className="w-3 h-3" />
        Opportunités récentes ({opps.length})
      </p>
      {opps.map((o) => (
        <div
          key={o.sellsy_id}
          className="bg-white border border-slate-200 rounded-md px-2 py-1.5 space-y-1.5"
          data-testid={`contact-opp-${o.sellsy_id}`}
        >
          <div className="flex items-center justify-between gap-2 min-w-0">
            <a
              href={o.sellsy_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-slate-700 hover:text-blue-700 truncate inline-flex items-center gap-1 min-w-0"
              title={o.name}
              data-testid={`contact-opp-link-${o.sellsy_id}`}
            >
              <span className="truncate">{o.name || `Opportunité #${o.sellsy_id}`}</span>
              <ExternalLink className="w-3 h-3 flex-shrink-0 text-slate-400" />
            </a>
          </div>
          <div className="flex items-center gap-1.5">
            {o.pipeline_name && (
              <span className="text-[10px] text-slate-400 truncate" title={o.pipeline_name}>
                {o.pipeline_name}
              </span>
            )}
            <select
              value={o.current_step_id || ""}
              disabled={savingByOppId[o.sellsy_id]}
              onChange={(e) => handleStepChange(o.sellsy_id, e.target.value, o.name)}
              className="flex-1 text-xs border border-slate-200 rounded px-1.5 py-0.5 bg-slate-50 hover:bg-white focus:outline-none focus:ring-1 focus:ring-blue-300 disabled:opacity-50 min-w-0"
              data-testid={`contact-opp-step-${o.sellsy_id}`}
            >
              {(!o.available_steps || o.available_steps.length === 0) && (
                <option value="">{o.current_step_name || "—"}</option>
              )}
              {o.available_steps && o.available_steps.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {savingByOppId[o.sellsy_id] && <Loader2 className="w-3 h-3 animate-spin text-slate-400 flex-shrink-0" />}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ContactOpportunitiesBlock;
